import { Injectable, NgZone } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import {
  BiometricSecurityStrength,
  BrowserVault,
  Device,
  DeviceSecurityType,
  IdentityVaultConfig,
  Vault,
  VaultError,
  VaultErrorCodes,
  VaultType,
} from '@ionic-enterprise/identity-vault';
import { PrivacyScreen } from '@capacitor-community/privacy-screen';
import { APPCONSTANTS } from './app-service.constants';
import { RouteService } from './route.service';
import { Platform, ToastController } from '@ionic/angular';

const authToken = 'authToken';
const refreshToken = 'refreshToken';
const userObject = 'userObject';

export interface VaultServiceState {
  session: any;
  userObject: any;
  authToken: string | undefined;
  refreshToken: string | undefined;
  privacyScreen: boolean;
  isLocked: boolean;
  lockType: 'NoLocking' | 'Biometrics' | 'SystemPasscode' | 'Both';
  canUseBiometrics: boolean;
  canUsePasscode: boolean;
  isEmpty: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class VaultService {
  config: IdentityVaultConfig = {
    key: 'gov.georgia.dhs.dcss.cspMobile',
    type: VaultType.DeviceSecurity,
    deviceSecurityType: DeviceSecurityType.Both,
    lockAfterBackgrounded: 2000,
    androidBiometricsPromptDescription: 'Please Scan Finger Print',
    iosBiometricsLocalizedReason: 'Please Scan Finger Print',
    shouldClearVaultAfterTooManyFailedAttempts: true,
    customPasscodeInvalidUnlockAttempts: 500,
    unlockVaultOnLoad: false,
  };
  // Secure Storage Vault used for the UserObject
  ssConfig: IdentityVaultConfig = {
    key: 'gov.georgia.dhs.dcss.cspMobile.secure',
    type: VaultType.SecureStorage,
    deviceSecurityType: DeviceSecurityType.None,
    unlockVaultOnLoad: false,
  };

  public state: VaultServiceState = {
    session: null,
    privacyScreen: false,
    isLocked: false,
    lockType: 'Both',
    canUseBiometrics: false,
    canUsePasscode: true,
    isEmpty: false,
    authToken: '',
    refreshToken: '',
    userObject: null,
  };

  vault!: Vault | BrowserVault;
  ssVault!: Vault | BrowserVault;
  constructor(
    private routeService: RouteService,
    private ngZone: NgZone,
    private toastController: ToastController
  ) { }

  /**
   * Init is called by our APP_INITIALIZER at the startup of the application
   */
  public async init() {
    if (Capacitor.getPlatform() === 'web') {
      this.ssVault = new BrowserVault();
      this.vault = new BrowserVault();
    } else {
      // If the device doesnt have biometrics the we'll use a Secure Storage Vault
      if (Capacitor.getPlatform() === 'android') {
        this.config.key = 'com.cspMobile.DCSSMobileApp';
      }
      if (!(await this.hasBiometrics())) {
        this.config = {
          ...this.config,
          type: VaultType.SecureStorage,
          deviceSecurityType: DeviceSecurityType.None,
        };
      }
      this.vault = new Vault(this.config);
      await this.vault.initialize(this.config);
      this.ssVault = new Vault();
      await this.ssVault.initialize(this.ssConfig);
    }

    this.vault.onConfigChanged(() => { });

    this.vault.onLock(() => {
      this.ngZone.run(() => {
        this.state.isLocked = true;
        this.state.session = undefined;
      });
    });
    this.vault.onUnlock(() => {
      this.ngZone.run(() => {
        this.state.isLocked = false;
      });
    });

    this.vault.onError(async (error: VaultError) => {
      let toast = this.toastController.create({
        message: error.message,
        duration: 3000,
        position: 'bottom',
      });
      toast.then(toast => toast.present());
      if (
        error.code === VaultErrorCodes.UserCanceledInteraction ||
        error.code === VaultErrorCodes.InvalidatedCredential ||
        error.code === VaultErrorCodes.AndroidUnexpectedKeystoreError
      ) {
        try {
          await this.vault.clear();
          await this.ssVault.clear();
        } finally {
          this.routeService.returnToLogin();
        }
      }
    });

    this.state.isLocked = await this.vault.isLocked();

    if (Capacitor.getPlatform() !== APPCONSTANTS.WEB) {
      await PrivacyScreen.disable();
    }

    this.state.privacyScreen = false;
    this.state.canUseBiometrics =
      Capacitor.getPlatform() === APPCONSTANTS.WEB
        ? false
        : await Device.isBiometricsEnabled();
    this.state.canUsePasscode =
      Capacitor.getPlatform() === APPCONSTANTS.WEB
        ? false
        : await Device.isSystemPasscodeSet();
    this.state.isEmpty = await this.vault.isEmpty();
  }
  private async hasBiometrics(): Promise<boolean> {
    // For this app we only want to use biometrics if the device is capable of strong encryption
    return (
      (await Device.isBiometricsEnabled()) &&
      (await Device.getBiometricStrengthLevel()) ===
      BiometricSecurityStrength.Strong
    );
  }

  async setKeyValue(key: string, value?: string): Promise<void> {
    if (value) {
      await this.theRightVault(key).setValue(key, value);
    } else {
      await this.theRightVault(key).setValue(key, '');
    }
  }
  async getKeyValue(key: string) {
    return await this.theRightVault(key).getValue(key);
  }
  theRightVault(key: string): Vault | BrowserVault {
    if (key == authToken || key == refreshToken) {
      // Use the biometric vault for auth tokens
      return this.vault;
    } else {
      // Use the Secure Storage Vault for all other data
      return this.ssVault;
    }
  }

  async setAuthToken(value: string): Promise<void> {
    this.state.authToken = value;
    await this.setKeyValue(authToken, value);
  }
  async setRefreshToken(value: string): Promise<void> {
    this.state.refreshToken = value;
    await this.setKeyValue(refreshToken, value);
  }
  async getAuthToken(): Promise<string | undefined> {
    const value = await this.getKeyValue(authToken);
    this.state.authToken = value;
    return value;
  }
  async getRefreshToken() {
    const value = await this.getKeyValue(refreshToken);
    this.state.refreshToken = value;
    return value;
  }
  async getUserObject() {
    const value = await this.getKeyValue(userObject);
    this.state.userObject = value;
    return value;
  }

  async setLockType() {
    let type: VaultType;
    let deviceSecurityType: DeviceSecurityType;

    switch (this.state.lockType) {
      case 'Biometrics':
        type = VaultType.DeviceSecurity;
        deviceSecurityType = DeviceSecurityType.Biometrics;
        break;

      case 'SystemPasscode':
        type = VaultType.DeviceSecurity;
        deviceSecurityType = DeviceSecurityType.SystemPasscode;
        break;

      case 'Both':
        type = VaultType.DeviceSecurity;
        deviceSecurityType = DeviceSecurityType.Both;
        break;

      default:
        type = VaultType.SecureStorage;
        deviceSecurityType = DeviceSecurityType.None;
    }
    this.config.type = type;
    this.config.deviceSecurityType = deviceSecurityType;
    await this.vault.updateConfig(this.config);
  }
  async updateConfig() {
    let pKey = 'gov.georgia.dhs.dcss.cspMobile';
    if (Capacitor.getPlatform() === APPCONSTANTS.ANDROID) {
      pKey = 'com.cspMobile.DCSSMobileApp';
    }
    const updateConfiguration: IdentityVaultConfig = {
      key: pKey,
      type: VaultType.DeviceSecurity,
      deviceSecurityType: DeviceSecurityType.None,
      unlockVaultOnLoad: false,
    };
    this.vault = new Vault(updateConfiguration);
    this.vault.config = updateConfiguration;
    this.state.lockType = 'NoLocking';
    await this.vault.initialize(updateConfiguration);
    await this.setLockType();
  }
  async resetConfig() {
    this.state.lockType = 'Both';
    this.vault = new Vault();
    this.ssVault = new Vault();
    this.vault.config = this.config;
    this.ssVault.config = this.ssConfig;
    await this.ssVault.initialize(this.ssConfig);
    await this.vault.initialize(this.config);
    await this.setLockType();
  }
  async removeKey(key: string) {
    await this.theRightVault(key)?.removeValue(key);
  }
  async clearVault() {
    this.state.session = undefined;
    this.state.isEmpty = true;
    this.state.lockType = 'Both';
    await this.vault.clear();
    await this.ssVault.clear();
  }

  public async toggleLockAfterBackgrounded(shouldEnable: boolean) {
    try {
      if (!this.vault) {
        console.warn(
          'toggleLockAfterBackgrounded called before vault initialization; skipping update'
        );
        return;
      }
      const newConfig = shouldEnable
        ? this.config
        : { ...this.config, lockAfterBackgrounded: undefined };
      await this.vault.updateConfig(newConfig);
    } catch (err) {
      // Swallow errors so callers don't need to handle vault config failures;
      // From here, should consider logging for diagnostics instead of propagating.
      console.error('Failed to update lockAfterBackgrounded setting', err);
    }
  }
}
